// src/pages/edu/EduYoutube.jsx
import React, { useEffect, useState } from "react";
import api from "../../api";

const KIND = "youtube";
const ACCEPT = ".xls,.xlsx,.pdf,.hwp,.hwpx,.ppt,.pptx,.zip";

/* 파일 URL을 API 서버 기준 절대경로로 변환 */
function resolveFileUrl(url) {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return base + url;
}

/* ───────── 별점 표시/입력 ───────── */
function Stars({ value = 0, onChange }) {
  return (
    <span style={{ display: "inline-flex", gap: 2, cursor: onChange ? "pointer" : "default" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} onClick={onChange ? () => onChange(n) : undefined}
          style={{ color: n <= value ? "#f5b301" : "#ccc", fontSize: 18, lineHeight: 1 }}>★</span>
      ))}
    </span>
  );
}

export default function EduYoutube() {
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ channel: "", title: "", content: "", link: "", category: "", rating: 0 });
  const [newCat, setNewCat] = useState("");
  const [formMaterials, setFormMaterials] = useState([]);   // 등록 시 첨부할 교육자료

  const [me, setMe] = useState(null);
  const rank = parseInt(me?.position_rank, 10);
  const canEdit = rank === 2 || rank === 3;
  const canDelete = rank === 2;

  const fetchList = () => {
    setLoading(true);
    const params = { kind: KIND };
    if (search) params.search = search;
    if (catFilter) params.category = catFilter;
    api.get("education/courses/", { params })
      .then((r) => setList(r.data.results || r.data))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    api.get("education/categories/")
      .then((r) => setCategories(r.data.results || r.data))
      .catch(() => setCategories([]));
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => {
    api.get("/auth/me/").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [catFilter]);

  const onSearch = (e) => { e.preventDefault(); fetchList(); };

  const addCategory = () => {
    const name = newCat.trim();
    if (!name) return;
    api.post("education/categories/", { name })
      .then(() => { setNewCat(""); fetchCategories(); })
      .catch((err) => alert(err?.response?.data?.name?.[0] || "분류 추가 실패"));
  };

  const createCourse = () => {
    if (!form.title.trim()) { alert("영상 이름을 입력하세요."); return; }
    api.post("education/courses/", {
      kind: KIND,
      channel: form.channel,
      title: form.title,
      content: form.content,
      link: form.link,
      category: form.category || null,
      rating: form.rating,
    })
      .then((r) => {
        const newId = r.data.id;
        return uploadFilesTo(newId, "material", formMaterials);
      })
      .then(() => {
        setForm({ channel: "", title: "", content: "", link: "", category: "", rating: 0 });
        setFormMaterials([]);
        fetchList();
      })
      .catch((err) => alert(err?.response?.data?.file?.[0] || "등록 실패"));
  };

  const deleteCourse = (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    api.delete(`education/courses/${id}/`).then(fetchList);
  };

  const updateCourse = (id, payload) => {
    return api.patch(`education/courses/${id}/`, payload)
      .then(fetchList)
      .catch(() => alert("수정 실패"));
  };

  const vote = (id, type) => {
    api.post(`education/courses/${id}/${type}/`).then(fetchList);
  };

  const uploadFilesTo = (courseId, fileType, files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return Promise.resolve();
    return Promise.all(
      arr.map((file) => {
        const fd = new FormData();
        fd.append("course", courseId);
        fd.append("file_type", fileType);
        fd.append("file", file);
        return api.post("education/files/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      })
    );
  };

  const uploadFile = (courseId, fileType, files) => {
    uploadFilesTo(courseId, fileType, files)
      .then(fetchList)
      .catch((err) => alert(err?.response?.data?.file?.[0] || "업로드 실패 (엑셀/PDF/한글/PPT/ZIP만 가능)"));
  };

  const deleteFile = (fileId) => {
    if (!window.confirm("파일을 삭제하시겠습니까?")) return;
    api.delete(`education/files/${fileId}/`).then(fetchList);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 16 }}>유튜브 강의</h2>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ───────── 왼쪽: 등록 영역 ───────── */}
        <div style={{ flex: "0 0 360px", maxWidth: 360, minWidth: 300 }}>
          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>영상 등록</div>

            <div style={{ display: "grid", gap: 8 }}>
              {/* 분류 선택 + 새 분류 추가 (한 줄) */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  style={{ ...inp, flex: "0 0 auto", width: 130 }}>
                  <option value="">분류 선택</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
                <input value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  placeholder="새 분류"
                  style={{ flex: "1 1 auto", minWidth: 0, padding: "8px 8px", border: "1px solid #ccc", borderRadius: 6 }} />
                <button type="button" onClick={addCategory} style={btn("#888", 13)}>추가</button>
              </div>

              <input placeholder="채널명" value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value })} style={inp} />
              <input placeholder="영상 이름" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} style={inp} />
              <textarea placeholder="영상 내용" value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6}
                style={{ ...inp, resize: "vertical", minHeight: 120 }} />
              <input placeholder="교육링크 (https://youtu.be/...)" value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })} style={inp} />
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#666" }}>별점</span>
                <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
              </div>

              <FormFilePicker label="교육자료" files={formMaterials} setFiles={setFormMaterials} />

              <button type="button" onClick={createCourse} style={btn("#2d6cdf")}>저장</button>
            </div>
          </div>
        </div>

        {/* ───────── 오른쪽: 검색 + 목록 ───────── */}
        <div style={{ flex: "1 1 0", minWidth: 320 }}>
          <form onSubmit={onSearch} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "nowrap" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="전체 검색 (채널·영상명·내용·분류·댓글 등)"
              style={{ flex: "1 1 auto", minWidth: 0, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6 }} />
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
              style={{ flex: "0 0 auto", width: 130, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6 }}>
              <option value="">전체 분류</option>
              {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <button type="submit" style={btn("#2d6cdf")}>검색</button>
          </form>

          {loading && <p style={{ color: "#888" }}>불러오는 중…</p>}
          {!loading && list.length === 0 && <p style={{ color: "#888" }}>등록된 영상이 없습니다.</p>}

          {list.map((c, idx) => (
            <CourseCard key={c.id} course={c} index={list.length - idx}
              categories={categories}
              canEdit={canEdit} canDelete={canDelete}
              onVote={vote} onDelete={deleteCourse} onUpdate={updateCourse}
              onUpload={uploadFile} onDeleteFile={deleteFile} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course, index, categories = [], canEdit, canDelete, onVote, onDelete, onUpdate, onUpload, onDeleteFile }) {
  const materials = course.files?.filter((f) => f.file_type === "material") || [];

  const [comments, setComments] = useState(course.comments || []);
  const [cBody, setCBody] = useState("");
  const [cRating, setCRating] = useState(5);

  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({
    channel: course.channel || "",
    title: course.title || "",
    content: course.content || "",
    link: course.link || "",
    category: course.category || "",
    rating: course.rating || 0,
    author_name: course.author_name || "",
  });

  const saveEdit = () => {
    onUpdate(course.id, {
      channel: edit.channel,
      title: edit.title,
      content: edit.content,
      link: edit.link,
      category: edit.category || null,
      rating: edit.rating,
      author_name: edit.author_name,
    }).then(() => setEditing(false));
  };

  const addComment = () => {
    if (!cBody.trim()) return;
    api.post("education/comments/", { course: course.id, body: cBody, rating: cRating })
      .then((r) => { setComments([r.data, ...comments]); setCBody(""); setCRating(5); })
      .catch(() => alert("댓글 등록 실패"));
  };

  const delComment = (id) => {
    api.delete(`education/comments/${id}/`).then(() => setComments(comments.filter((x) => x.id !== id)));
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "#2d6cdf" }}>#{index}</span>
          {course.category_name && (<span style={tag}>{course.category_name}</span>)}
          <span style={{ fontSize: 12, color: "#999" }}>{course.created_at?.slice(0, 10)}</span>
          {!editing && course.author_name && (
            <span style={{ fontSize: 12, color: "#666" }}>작성자: {course.author_name}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} style={btn("#2d6cdf", 12)}>수정</button>
          )}
          {editing && (
            <>
              <button onClick={saveEdit} style={btn("#27ae60", 12)}>저장</button>
              <button onClick={() => setEditing(false)} style={btn("#999", 12)}>취소</button>
            </>
          )}
          {canDelete && !editing && (
            <button onClick={() => onDelete(course.id)} style={btn("#e74c3c", 12)}>삭제</button>
          )}
        </div>
      </div>

      {editing ? (
        <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>작성자</span>
            <input value={edit.author_name} onChange={(e) => setEdit({ ...edit, author_name: e.target.value })}
              style={{ ...inp, width: 160 }} />
            <select value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })}
              style={{ ...inp, width: 140 }}>
              <option value="">분류 선택</option>
              {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <input value={edit.channel} onChange={(e) => setEdit({ ...edit, channel: e.target.value })}
            placeholder="채널명" style={inp} />
          <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
            placeholder="영상 이름" style={inp} />
          <textarea value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })}
            rows={4} placeholder="영상 내용" style={{ ...inp, resize: "vertical" }} />
          <input value={edit.link} onChange={(e) => setEdit({ ...edit, link: e.target.value })}
            placeholder="교육링크" style={inp} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#666" }}>별점</span>
            <Stars value={edit.rating} onChange={(n) => setEdit({ ...edit, rating: n })} />
          </div>
        </div>
      ) : (
        <>
          {course.channel && (
            <div style={{ fontSize: 13, color: "#c0392b", fontWeight: 600, marginBottom: 2 }}>▶ {course.channel}</div>
          )}
          <h3 style={{ margin: "4px 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{course.title}</h3>
          <p style={{ color: "#444", margin: "4px 0 10px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{course.content}</p>

          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Stars value={course.rating} /> <small style={{ color: "#888" }}>({course.rating})</small>
            </span>
            <button onClick={() => onVote(course.id, "like")} style={btn("#27ae60", 13)}>👍 {course.like_count}</button>
            <button onClick={() => onVote(course.id, "dislike")} style={btn("#999", 13)}>👎 {course.dislike_count}</button>
            {course.link && (
              <a href={course.link} target="_blank" rel="noreferrer"
                style={{ ...btnStyleBase("#c0392b", 13), textDecoration: "none" }}>▶ 영상 보기</a>
            )}
          </div>
        </>
      )}

      <FileSection title="교육자료" files={materials} editing={editing}
        onUpload={(f) => onUpload(course.id, "material", f)} onDeleteFile={onDeleteFile} />

      <div style={{ marginTop: 14, borderTop: "1px solid #eee", paddingTop: 10 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>댓글 {comments.length}</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10 }}>
          <input value={cBody} onChange={(e) => setCBody(e.target.value)} placeholder="댓글 추가..."
            style={{ flex: 1, padding: "6px 8px", border: "1px solid #ddd", borderRadius: 6 }} />
          <Stars value={cRating} onChange={setCRating} />
          <button onClick={addComment} style={btn("#2d6cdf", 13)}>등록</button>
        </div>
        {comments.map((cm) => (
          <div key={cm.id} style={{ padding: "6px 0", borderBottom: "1px solid #f4f4f4" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <strong style={{ fontSize: 13 }}>{cm.author_name || "익명"}</strong>{" "}
                <small style={{ color: "#999" }}>{cm.created_at?.slice(0, 10)}</small>{" "}
                <Stars value={cm.rating} />
              </div>
              <button onClick={() => delComment(cm.id)}
                style={{ border: "none", background: "none", color: "#bbb", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ fontSize: 14, color: "#333" }}>{cm.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormFilePicker({ label, files, setFiles }) {
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };
  const removeAt = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>{label}</span>
        <label style={{ ...btnStyleBase("#34495e", 12), cursor: "pointer" }}>
          파일 선택
          <input type="file" accept={ACCEPT} multiple style={{ display: "none" }} onChange={onPick} />
        </label>
      </div>
      {files.length > 0 && (
        <ul style={{ margin: "4px 0 0", padding: 0, listStyle: "none" }}>
          {files.map((f, i) => (
            <li key={i} style={{ fontSize: 12, color: "#333", padding: "2px 0",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span title={f.name}>📎 {f.name}</span>
              <button type="button" onClick={() => removeAt(i)}
                style={{ border: "none", background: "none", color: "#bbb", cursor: "pointer", marginLeft: 4 }}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileSection({ title, files, editing, onUpload, onDeleteFile }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
        {editing && (
          <label style={{ ...btnStyleBase("#34495e", 12), cursor: "pointer" }}>
            업로드
            <input type="file" accept={ACCEPT} multiple style={{ display: "none" }}
              onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
      {files.length === 0 ? (
        <small style={{ color: "#aaa" }}>첨부 없음</small>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
          {files.map((f) => {
            const fname = f.original_name || (f.file_url ? decodeURIComponent(f.file_url.split("/").pop()) : "(파일명 없음)");
            return (
            <li key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0", flexWrap: "wrap" }}>
              <a href={resolveFileUrl(f.file_url)} download={fname} title={fname}
                style={{ ...btnStyleBase("#2d6cdf", 12), textDecoration: "none" }}>⬇ 다운로드</a>
              <span title={fname} style={{ color: "#333", wordBreak: "break-all" }}>{fname}</span>
              {editing && (
                <button onClick={() => onDeleteFile(f.id)}
                  style={{ border: "none", background: "none", color: "#bbb", cursor: "pointer", flexShrink: 0 }}>×</button>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function btnStyleBase(bg, fontSize = 14) {
  return { background: bg, color: "#fff", border: "none", borderRadius: 6,
    padding: "6px 14px", fontSize, cursor: "pointer", fontWeight: 600,
    whiteSpace: "nowrap", flexGrow: 0, flexShrink: 0, width: "auto",
    display: "inline-flex", alignItems: "center", justifyContent: "center" };
}
function btn(bg, fontSize = 14) { return btnStyleBase(bg, fontSize); }

const card = { border: "1px solid #e5e5e5", borderRadius: 10, padding: 16,
  marginBottom: 16, background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" };
const inp = { padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, width: "100%", boxSizing: "border-box" };
const tag = { background: "#eef3ff", color: "#2d6cdf", borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 };